// Shared lists (#286) — pure rules shared by every screen that needs to know
// whether a List is shared, whether a Task can be moved or deleted, and what
// order Lists should appear in. Mirrors ios/JuDI/Models/ListSharing.swift so
// the same rules exist in exactly one place per platform, rather than being
// re-derived (and risking drift) at every call site.

import type { List, ListMember, Task } from "../types";

// A missing member list must stay this one array. A fresh [] on each
// render makes every board memo look new, and the board then updates forever.
export const EMPTY_LIST_MEMBERS: ListMember[] = [];

/** True when a List has any membership row at all — an Invitation counts,
 * matching CONTEXT.md's "a List counts as shared from the moment an
 * Invitation is sent" and the database's own free-tier predicate
 * (user_active_task_count). Used for the "shared" mark shown on an Owner's
 * own List. */
export function isListShared(listId: string | null, members: ListMember[]): boolean {
  if (!listId) return false;
  return members.some((m) => m.list_id === listId);
}

/** Every List id this person has *accepted* Membership on — not lists they
 * own, and not an unanswered Invitation. This is "Lists shared with me". */
export function acceptedMemberListIds(members: ListMember[], myUserId: string): Set<string> {
  return new Set(
    members
      .filter((m) => m.user_id === myUserId && m.status === "accepted")
      .map((m) => m.list_id),
  );
}

/** Every unanswered Invitation addressed to this person. */
export function myPendingInvitations(members: ListMember[], myUserId: string): ListMember[] {
  return members.filter((m) => m.user_id === myUserId && m.status === "invited");
}

/**
 * Whether `viewerId` may move this Task to a different List or Delete it
 * (ADR-0003's one restriction on an otherwise-fully-shared Task): the
 * Task's Author always may; otherwise only the List's Owner may, and a
 * Listless Task has no Owner to fall back on. Mirrors
 * enforce_task_move_restriction and enforce_task_move_restriction's sibling
 * DELETE policy exactly, so the UI never offers a button the database would
 * refuse.
 */
export function canMoveOrDelete(
  task: Pick<Task, "user_id" | "list_id">,
  viewerId: string,
  lists: List[],
): boolean {
  if (task.user_id === viewerId) return true;
  if (!task.list_id) return false;
  const list = lists.find((l) => l.id === task.list_id);
  return !!list && list.user_id === viewerId;
}

/**
 * Board column order: owned Lists first, in their existing order, then
 * accepted-Membership Lists pinned after — a Member can't rearrange a
 * Shared list (docs/shared-lists-deferred-edges.md, "A Member cannot
 * arrange their own board"). A List with only an unanswered Invitation is
 * left out entirely: there's nothing to do about it yet except answer the
 * Invitation, the same gap iOS's Session E filled the same way.
 */
export function orderedListsForBoard(
  lists: List[],
  members: ListMember[],
  myUserId: string,
): List[] {
  const accepted = acceptedMemberListIds(members, myUserId);
  const owned = lists.filter((l) => l.user_id === myUserId);
  const sharedWithMe = lists.filter((l) => l.user_id !== myUserId && accepted.has(l.id));
  return [...owned, ...sharedWithMe];
}

/** The List name an invited person is allowed to see — emoji + name, or a
 * fallback when the List row has not loaded yet. */
export function invitationListLabel(list: List | undefined): string {
  return list ? `${list.emoji} ${list.name}` : "a shared list";
}

/** Who sent the Invitation. JuDI has no display names, so this is the
 * Owner's email; "Someone" covers a lookup still in flight or gone. */
export function invitationSenderLabel(ownerEmail: string | null | undefined): string {
  return ownerEmail || "Someone";
}
